import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    CardTitle,
    Button,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    FormFeedback,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
    getAdminCompanyUsers,
    addAdminCompanyUser,
    updateAdminCompanyUser,
    deleteAdminCompanyUser,
    getCompanyRoles,
} from "../../../helpers/fakebackend_helper";

const CompanyUsersManager = ({ companyId }) => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadUsers();
        loadRoles();
    }, [companyId]);

    const loadRoles = async () => {
        try {
            const response = await getCompanyRoles();
            // Filter only company roles (not system roles)
            const companyRoles = response.filter(role =>
                ['company-admin', 'manager', 'agent', 'viewer'].includes(role.slug)
            );
            setRoles(companyRoles);
        } catch (error) {
            console.error("Error loading roles:", error);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await getAdminCompanyUsers(companyId);
            setUsers(response);
        } catch (error) {
            toast.error("Erreur lors du chargement des utilisateurs");
            console.error(error);
        }
    };

    const toggleModal = () => {
        setModal(!modal);
        if (!modal) {
            formik.resetForm();
            setIsEdit(false);
            setCurrentUser(null);
        }
    };

    const handleEdit = (user) => {
        setCurrentUser(user);
        setIsEdit(true);
        formik.setValues({
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            password: "",
            role_id: user.role_id,
            department: user.department || "",
            job_title: user.job_title || "",
            status: user.status,
        });
        setModal(true);
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir retirer cet utilisateur ?")) {
            return;
        }

        try {
            await deleteAdminCompanyUser(companyId, userId);
            toast.success("Utilisateur retiré de l'entreprise");
            loadUsers();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
            console.error(error);
        }
    };

    const validationSchema = Yup.object({
        first_name: Yup.string().required("Prénom requis"),
        last_name: Yup.string().required("Nom requis"),
        email: Yup.string().email("Email invalide").required("Email requis"),
        password: isEdit
            ? Yup.string().min(8, "Minimum 8 caractères")
            : Yup.string().min(8, "Minimum 8 caractères").required("Mot de passe requis"),
        role_id: Yup.number().required("Rôle requis"),
        department: Yup.string(),
        job_title: Yup.string(),
        status: Yup.string(),
    });

    const formik = useFormik({
        enableReinitialize: true, // Important: update values when roles are loaded
        initialValues: {
            first_name: "",
            last_name: "",
            email: "",
            password: "",
            role_id: roles.length > 0 ? roles[0].id : "",
            department: "",
            job_title: "",
            status: "active",
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (isEdit) {
                    await updateAdminCompanyUser(companyId, currentUser.id, values);
                    toast.success("Utilisateur mis à jour");
                } else {
                    await addAdminCompanyUser(companyId, values);
                    toast.success("Utilisateur créé avec succès");
                }
                toggleModal();
                loadUsers();
            } catch (error) {
                const message =
                    error.response?.data?.message ||
                    error.message ||
                    "Erreur lors de l'opération";
                toast.error(message);
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Card>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <CardTitle className="h4 mb-0">Utilisateurs de l'Entreprise</CardTitle>
                    <Button color="primary" onClick={toggleModal}>
                        <i className="mdi mdi-plus me-1"></i> Ajouter un utilisateur
                    </Button>
                </div>

                <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Nom</th>
                                <th>Email</th>
                                <th>Rôle</th>
                                <th>Département</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div className="avatar-xs me-3">
                                                <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-16">
                                                    {user.first_name.charAt(0)}
                                                </span>
                                            </div>
                                            <div>
                                                {user.first_name} {user.last_name}
                                                {user.is_owner && (
                                                    <Badge color="warning" className="ms-2 font-size-10">
                                                        Propriétaire
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{user.email}</td>
                                    <td>
                                        <Badge color="info" className="font-size-11">
                                            {user.role_name}
                                        </Badge>
                                    </td>
                                    <td>{user.department || "-"}</td>
                                    <td>
                                        <Badge
                                            color={user.status === "active" ? "success" : "danger"}
                                            className="font-size-11"
                                        >
                                            {user.status === "active" ? "Actif" : "Inactif"}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEdit(user)}
                                        >
                                            <i className="mdi mdi-pencil"></i>
                                        </Button>
                                        {!user.is_owner && (
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleDelete(user.id)}
                                            >
                                                <i className="mdi mdi-delete"></i>
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {users.length === 0 && (
                        <div className="text-center py-5 text-muted">
                            Aucun utilisateur trouvé
                        </div>
                    )}
                </div>
            </CardBody>

            {/* Modal pour Créer/Modifier */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {isEdit ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="first_name">Prénom *</Label>
                                    <Input
                                        id="first_name"
                                        name="first_name"
                                        type="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.first_name}
                                        invalid={
                                            formik.touched.first_name && formik.errors.first_name
                                        }
                                    />
                                    <FormFeedback>{formik.errors.first_name}</FormFeedback>
                                </FormGroup>
                            </div>

                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="last_name">Nom *</Label>
                                    <Input
                                        id="last_name"
                                        name="last_name"
                                        type="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.last_name}
                                        invalid={
                                            formik.touched.last_name && formik.errors.last_name
                                        }
                                    />
                                    <FormFeedback>{formik.errors.last_name}</FormFeedback>
                                </FormGroup>
                            </div>
                        </div>

                        <FormGroup>
                            <Label for="email">Email *</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                invalid={formik.touched.email && formik.errors.email}
                            />
                            <FormFeedback>{formik.errors.email}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="password">
                                Mot de passe {isEdit && "(Laisser vide pour ne pas modifier)"}
                            </Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                                invalid={formik.touched.password && formik.errors.password}
                            />
                            <FormFeedback>{formik.errors.password}</FormFeedback>
                        </FormGroup>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="role_id">Rôle *</Label>
                                    <Input
                                        id="role_id"
                                        name="role_id"
                                        type="select"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.role_id}
                                        invalid={formik.touched.role_id && formik.errors.role_id}
                                    >
                                        {roles.map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{formik.errors.role_id}</FormFeedback>
                                </FormGroup>
                            </div>

                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="department">Département</Label>
                                    <Input
                                        id="department"
                                        name="department"
                                        type="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.department}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="job_title">Poste</Label>
                                    <Input
                                        id="job_title"
                                        name="job_title"
                                        type="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.job_title}
                                    />
                                </FormGroup>
                            </div>

                            {isEdit && (
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label for="status">Statut</Label>
                                        <Input
                                            id="status"
                                            name="status"
                                            type="select"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.status}
                                        >
                                            <option value="active">Actif</option>
                                            <option value="inactive">Inactif</option>
                                        </Input>
                                    </FormGroup>
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button type="button" color="secondary" onClick={toggleModal}>
                                Annuler
                            </Button>
                            <Button type="submit" color="primary" disabled={loading}>
                                {loading ? "..." : isEdit ? "Mettre à jour" : "Créer"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </Card>
    );
};

export default CompanyUsersManager;
